import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiPost, useApiPut, useApiDelete, useApiMutation, useApiBlobMutation } from '../../hooks/use-api';
import {
  PlatformExerciseListResponse,
  PlatformExerciseDetailResponse,
  CreatePlatformExerciseRequest,
  UpdatePlatformExerciseRequest,
  EquipmentOption
} from './types';

// Query Keys
export const platformExerciseKeys = {
  all: ['platform-exercises'],
  lists: () => [...platformExerciseKeys.all, 'list'],
  list: (filters: Record<string, any>) => [...platformExerciseKeys.lists(), JSON.stringify(filters)],
  details: () => [...platformExerciseKeys.all, 'detail'],
  detail: (id: number) => [...platformExerciseKeys.details(), id.toString()],
  categories: () => [...platformExerciseKeys.all, 'categories'],
  difficulties: () => [...platformExerciseKeys.all, 'difficulties'],
};

// Hooks for fetching data
export function usePlatformExerciseList(
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    category?: number;
    difficulty?: number;
    primaryEquipmentId?: number;
    includeInactive?: boolean;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  } = {}
) {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.category) queryParams.append('category', params.category.toString());
  if (params.difficulty) queryParams.append('difficulty', params.difficulty.toString());
  if (params.primaryEquipmentId) queryParams.append('primaryEquipmentId', params.primaryEquipmentId.toString());
  if (params.includeInactive) queryParams.append('includeInactive', params.includeInactive.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);

  const endpoint = `/platform/exercises${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return useApiQuery<PlatformExerciseListResponse>(
    platformExerciseKeys.list(params),
    endpoint
  );
}

export function usePlatformExerciseDetail(id: number) {
  return useApiQuery<PlatformExerciseDetailResponse>(
    platformExerciseKeys.detail(id),
    `/platform/exercises/${id}`,
    {
      enabled: !!id,
    }
  );
}

export function usePlatformExerciseCategories() {
  return useApiQuery<Array<{ value: number; name: string }>>(
    platformExerciseKeys.categories(),
    '/platform/exercises/categories'
  );
}

export function usePlatformExerciseDifficulties() {
  return useApiQuery<Array<{ value: number; name: string }>>(
    platformExerciseKeys.difficulties(),
    '/platform/exercises/difficulties'
  );
}

// Equipment options for dropdowns (reusing platform equipment)
export function usePlatformEquipmentOptions() {
  return useApiQuery<{
    equipment: EquipmentOption[];
    pagination: { page: number; pageSize: number; totalCount: number; totalPages: number };
  }>(
    ['platform-equipment', 'options'],
    '/platform/equipment?pageSize=100&includeInactive=false'
  );
}

// Hooks for mutations
export function useCreatePlatformExercise() {
  const queryClient = useQueryClient();

  return useApiPost<{ id: number; message: string }, any, CreatePlatformExerciseRequest>(
    '/platform/exercises',
    {
      onSuccess: () => {
        // Invalidate and refetch exercise list
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
      },
    }
  );
}

export function useUpdatePlatformExercise() {
  const queryClient = useQueryClient();

  return useApiPut<{ message: string }, any, UpdatePlatformExerciseRequest & { id: number }>(
    (variables) => `/platform/exercises/${variables.id}`,
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch exercise list and specific item
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.detail(variables.id) });
      },
    }
  );
}

export function useDeletePlatformExercise() {
  const queryClient = useQueryClient();

  return useApiDelete<{ message: string }, any, { id: number }>(
    (variables) => `/platform/exercises/${variables.id}`,
    {
      onSuccess: () => {
        // Invalidate and refetch exercise list
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
      },
    }
  );
}

export function useReactivatePlatformExercise() {
  const queryClient = useQueryClient();

  return useApiPost<{ message: string }, any, { id: number }>(
    '/platform/exercises/reactivate',
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch exercise list and specific item
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.detail(variables.id) });
      },
    }
  );
}

export function useReorderPlatformExercises() {
  const queryClient = useQueryClient();

  return useApiPost<{ message: string }, any, { exerciseIds: number[] }>(
    '/platform/exercises/reorder',
    {
      onSuccess: () => {
        // Invalidate and refetch exercise list
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
      },
    }
  );
}

// CSV Export/Import hooks
export function useExportPlatformExercisesCsv() {
  return useApiBlobMutation<any, {
    includeInactive?: boolean;
    search?: string;
    category?: number;
    difficulty?: number;
    primaryEquipmentId?: number;
  }>(
    'GET',
    (variables) => {
      const queryParams = new URLSearchParams();
      if (variables.includeInactive) queryParams.append('includeInactive', 'true');
      if (variables.search) queryParams.append('search', variables.search);
      if (variables.category) queryParams.append('category', variables.category.toString());
      if (variables.difficulty) queryParams.append('difficulty', variables.difficulty.toString());
      if (variables.primaryEquipmentId) queryParams.append('primaryEquipmentId', variables.primaryEquipmentId.toString());
      
      return `/platform/exercises/export/csv${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    }
  );
}

export function useImportPlatformExercisesCsv() {
  const queryClient = useQueryClient();

  return useApiMutation<
    { message: string; summary: { created: number; updated: number; skipped: number }; errors: string[] },
    any,
    { file: File }
  >(
    'POST',
    () => '/platform/exercises/import/csv',
    {
      onSuccess: () => {
        // Invalidate and refetch exercise list after import
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
      },
      requestType: 'multipart',
    }
  );
}

// Hard delete hook (permanent removal)
export function useHardDeletePlatformExercise() {
  const queryClient = useQueryClient();

  return useApiDelete<{ message: string }, any, { id: number }>(
    (variables) => `/platform/exercises/${variables.id}/hard`,
    {
      onSuccess: () => {
        // Invalidate and refetch exercise list
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
      },
    }
  );
}

// Bulk operations hook
export function useBulkOperationPlatformExercises() {
  const queryClient = useQueryClient();

  return useApiPost<
    { message: string; processed: number; errors?: string[] }, 
    any, 
    { exerciseIds: number[]; operation: BulkOperation }
  >(
    '/platform/exercises/bulk',
    {
      onSuccess: () => {
        // Invalidate and refetch exercise list
        queryClient.invalidateQueries({ queryKey: platformExerciseKeys.lists() });
      },
    }
  );
}

// Bulk operation enum matching backend
export enum BulkOperation {
  Deactivate = 1,
  Reactivate = 2,
  HardDelete = 3
}